export default class SessionManager {

    sessionTimeout = null;
    sessionLength = 60 * 60 * 1000; // 60 minutes
    // sessionLength = 60 * 1000; // 60 seconds for testing
    sessionToken = null;
    group = 1; // Default group, can be set via URL param or other logic

    constructor() {
        // Singleton pattern to ensure only one instance
        if (SessionManager.instance) {
            return SessionManager.instance;
        }
        SessionManager.instance = this;
    }
    async start(){
        console.log('🚀 SessionManager starting...');
        
        // On creation, check URL params for sessionId, otherwise use localStorage
        const sessionParams = new URLSearchParams(window.location.search);
        const sessionId = sessionParams.get('sessionId') || window.localStorage.getItem("sessionId") || 0;
        this.group = parseInt(sessionParams.get('group')) || 1; // Get group from URL or default to 1
        
        console.log('📋 Session details:', {
            sessionId: sessionId,
            group: this.group,
            fromURL: !!sessionParams.get('sessionId'),
            fromStorage: !!window.localStorage.getItem("sessionId")
        });
        
        this.setSessionId(sessionId);

        // On creation, check localStorage for sessionStart, otherwise set to now
        let sessionStart = window.localStorage.getItem("sessionStart") || Date.now();
        if(sessionStart - Date.now() > this.sessionLength) { // if sessionStart is more than 60 minutes ago, reset
            sessionStart = Date.now();
        }
        this.setSessionStart(sessionStart);
        this.startSessionTimeout();

        // Don't create session here - wait for group selection
        console.log('⏳ SessionManager initialized, waiting for group selection before creating session...');
    }
    end(){
        window.localStorage.clear();
    }
    setSessionId(id) {
        window.sessionId = id;
        window.localStorage.setItem("sessionId", id);
    }
    setSessionStart(timestamp) {
        window.sessionStart = timestamp;
        window.localStorage.setItem("sessionStart", timestamp);
    }
    setGroup(groupNumber) {
        this.group = groupNumber;
        window.localStorage.setItem("hypnobreaker_group", groupNumber.toString());
        console.log(`📊 Group set to: ${groupNumber}`);
    }
    
    async initializeSessionWithGroup(groupNumber) {
        console.log(`🎯 Initializing session with group: ${groupNumber}`);
        this.setGroup(groupNumber);
        
        // Now create the game session with the correct group
        console.log('🔧 Creating game session...');
        const sessionId = window.sessionId;
        if (sessionId && sessionId !== "0") {
            console.log('📋 Using provided sessionId:', sessionId);
        } else {
            console.log('🆕 No sessionId provided, will generate new session');
        }
        
        const result = await this.createGameSession();
        if (result) {
            console.log('✅ Game session created successfully, sessionToken:', this.sessionToken ? 'SET' : 'NOT SET');
        } else {
            console.warn('⚠️ Game session creation failed - data will be stored locally only');
        }
        return result;
    }
    resetSession(){
        window.localStorage.clear();
        console.log("Session timed out due to inactivity. Please reload the game.");
        location.reload(); 
    }
    startSessionTimeout() {
        const sessionEnd = parseInt(window.sessionStart) + this.sessionLength;
        const timeLeft = sessionEnd - Date.now();
        console.log(`Session will end in ${Math.round(timeLeft/1000)} seconds due to inactivity.`);
        if (timeLeft <= 0) return this.resetSession();
        this.sessionTimeout = setTimeout(this.resetSession, timeLeft); // 60 minutes inactivity timeout
        //set event listeners to reset timeout on activity

        document.addEventListener('click', this.resetSessionTimeout.bind(this));
        document.addEventListener('keydown', this.resetSessionTimeout.bind(this));
        document.addEventListener('mousemove', this.resetSessionTimeout.bind(this));
        document.addEventListener('touchstart', this.resetSessionTimeout.bind(this));
    }
    resetSessionTimeout() {
        if(this.sessionTimeout) {
            clearTimeout(this.sessionTimeout);
        }
    }
    clearSession() {
        window.localStorage.clear();
        window.sessionId = 0;
        window.sessionStart = null;
    }
    getSessionId() {
        return window.sessionId;
    }

    // API Integration Methods
    async createGameSession() {
        try {
            console.log('🔗 Attempting to create game session via API...');
            
            // Use sessionId from URL params only - send "not set" if empty
            let sessionId = window.sessionId;
            if (!sessionId || sessionId === "0") {
                console.log('ℹ️ No sessionId provided - using placeholder value');
                sessionId = "not set"; // Use placeholder for API requirement
            }
            
            console.log('📡 Request details:', {
                group: this.group,
                sessionId: sessionId,
                action: 'createSession'
            });
            
            const response = await fetch('/.netlify/functions/userSubmissions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    group: this.group, 
                    sessionId: sessionId,
                    action: 'createSession'
                })
            });

            console.log('📨 API response status:', response.status, response.statusText);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('❌ API response error:', errorText);
                throw new Error(`Failed to create game session: ${response.status} - ${errorText}`);
            }
            
            const data = await response.json();
            console.log('📦 API response received');
            
            if (data.sessionToken) {
                this.sessionToken = data.sessionToken;
                window.localStorage.setItem('sessionToken', this.sessionToken);
                console.log('✅ SessionToken set successfully:', this.sessionToken.substring(0, 20) + '...');
                // Note: Ignoring sessionId from API response - we use our own sessionId management
                return true;
            } else {
                console.warn('⚠️ No sessionToken in response data');
                return false;
            }
        } catch (error) {
            console.error('💥 Error creating game session:', error.message);
            console.error('Stack trace:', error);
            return false;
        }
    }

    async submitChallenge(challengeNumber, resultData) {
        // OPTIMIZATION: Only submit finale challenge (5) and reflection challenge (6) which contains all comprehensive game data
        // Individual challenges (1,2,3,4) are skipped to reduce API calls and data redundancy
        if (challengeNumber !== 5 && challengeNumber !== 6) {
            console.log(`⏭️ Skipping individual challenge ${challengeNumber} submission - data will be included in finale`);
            return true; // Return success to avoid breaking game flow
        }

        if (!this.sessionToken) {
            console.warn('No session token available, cannot submit challenge');
            return false;
        }

        try {
            const response = await fetch('/.netlify/functions/userSubmissions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    action: 'submitChallenge',
                    sessionToken: this.sessionToken,
                    challengeNumber,
                    resultData 
                })
            });

            if (!response.ok) throw new Error('Failed to submit challenge');
            
            const data = await response.json();
            console.log(`Challenge ${challengeNumber} submitted successfully:`, data);
            return true;
        } catch (error) {
            console.error(`Error submitting challenge ${challengeNumber}:`, error);
            return false;
        }
    }

    async getSessionSummary() {
        if (!this.sessionToken) return null;

        try {
            const response = await fetch('/.netlify/functions/userSubmissions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    action: 'getSessionSummary',
                    sessionToken: this.sessionToken
                })
            });
            if (!response.ok) throw new Error('Failed to get session summary');
            
            const data = await response.json();
            console.log('Session summary:', data);
            return data;
        } catch (error) {
            console.error('Error getting session summary:', error);
            return null;
        }
    }

    // Challenge convenience methods
    async submitVillageChallenge(data) {
        console.log('📍 Village challenge data stored locally - will be included in finale submission');
        return await this.submitChallenge(1, data);
    }

    async submitBuildingChallenge(data) {
        console.log('🏢 Building challenge data stored locally - will be included in finale submission');
        return await this.submitChallenge(2, data);
    }

    async submitBattleChallenge(data) {
        console.log('⚔️ Battle challenge data stored locally - will be included in finale submission');
        return await this.submitChallenge(3, data);
    }

    async submitFriendChallenge(data) {
        console.log('👥 Friend challenge data stored locally - will be included in finale submission');
        return await this.submitChallenge(4, data);
    }
    
    async submitReflectionChallenge(data) {
        console.log('🤔 Reflection challenge data stored locally - will be included in finale submission');
        return await this.submitChallenge(6, data);
    }

    async submitFinaleChallenge(data) {
        console.log('🏆 Submitting comprehensive finale challenge with all game data');
        return await this.submitChallenge(5, data);
    }

    // Game Data Storage Methods
    async storeGameData(gameData, sceneType) {
        console.log(`📊 Storing ${sceneType} data for friend: ${gameData.friend}`);
        console.log(`🔑 SessionToken status: ${this.sessionToken ? 'AVAILABLE' : 'MISSING'}`);
        
        try {
            // Store locally first
            this.storeGameDataLocally(gameData, sceneType);
            console.log(`💾 Local storage completed for ${sceneType}`);
            
            // Submit to server if session token available
            if (this.sessionToken) {
                console.log(`🚀 Attempting server storage for ${sceneType}...`);
                
                const submission = {
                    action: 'storeGameData',
                    sessionToken: this.sessionToken,
                    gameData: gameData,
                    sceneType: sceneType,
                    timestamp: Date.now()
                };
                
                const response = await fetch('/.netlify/functions/userSubmissions', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(submission)
                });
                
                if (!response.ok) {
                    const errorText = await response.text();
                    console.error(`❌ Server storage failed: ${response.status} - ${errorText}`);
                    throw new Error(`Failed to store game data on server: ${response.status}`);
                }
                
                console.log(`✅ ${sceneType} data stored successfully on server`);
                return true;
            } else {
                console.warn('⚠️ No session token available');
                console.warn('   Possible reasons:');
                console.warn('   - No sessionId in URL parameters');
                console.warn('   - createGameSession() failed');
                console.warn('   - Running in local/demo mode');
                console.warn('   📁 Data stored locally only');
                return true;
            }
        } catch (error) {
            console.error(`❌ Error storing ${sceneType} data:`, error.message);
            console.error('Stack trace:', error);
            return false;
        }
    }

    storeGameDataLocally(gameData, sceneType) {
        try {
            // Create storage key
            const storageKey = `hypnobreaker_${sceneType}_${gameData.friend}`;
            
            // Store individual game data
            localStorage.setItem(storageKey, JSON.stringify(gameData));
            
            // Also maintain a research data collection
            const existingResearchData = JSON.parse(localStorage.getItem('hypnobreaker_research_data') || '[]');
            existingResearchData.push({
                ...gameData,
                sceneType: sceneType,
                storedAt: Date.now(),
                sessionId: this.getSessionId()
            });
            localStorage.setItem('hypnobreaker_research_data', JSON.stringify(existingResearchData));
            
            console.log(`💾 ${sceneType} data stored locally for ${gameData.friend}`);
        } catch (error) {
            console.error('Error storing game data locally:', error);
        }
    }

    getStoredGameData(friendName, sceneType) {
        try {
            const storageKey = `hypnobreaker_${sceneType}_${friendName}`;
            const stored = localStorage.getItem(storageKey);
            return stored ? JSON.parse(stored) : null;
        } catch (error) {
            console.error('Error retrieving stored game data:', error);
            return null;
        }
    }

    collectAllGameData() {
        console.log("🔍 Collecting all game data from localStorage...");
        
        const allData = {
            challenges: [],
            reflections: [],
            researchData: [],
            friendsRescued: []
        };

        try {
            // Collect friend scene data (challenges)
            const friendNames = ['mo', 'sam', 'julia', 'liam', 'louisa'];
            friendNames.forEach(friendName => {
                const friendData = this.getStoredGameData(friendName, 'friendScene');
                if (friendData) {
                    allData.challenges.push({
                        friend: friendName,
                        sceneType: 'friendScene',
                        data: friendData
                    });
                    allData.friendsRescued.push(friendName);
                }

                // Collect reflection data if available
                const reflectionData = this.getStoredGameData(friendName, 'reflectionScene');
                if (reflectionData) {
                    allData.reflections.push({
                        friend: friendName,
                        sceneType: 'reflectionScene',
                        data: reflectionData
                    });
                }
            });

            // Collect comprehensive research data if available
            const researchDataString = localStorage.getItem('hypnobreaker_research_data');
            if (researchDataString) {
                const researchData = JSON.parse(researchDataString);
                allData.researchData = researchData;
            }

            // Collect any other stored game data
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith('hypnobreaker_') && 
                    !key.includes('_friendScene_') && 
                    !key.includes('_reflectionScene_') &&
                    key !== 'hypnobreaker_research_data' &&
                    key !== 'hypnobreaker_group' &&
                    key !== 'sessionId' &&
                    key !== 'sessionStart' &&
                    key !== 'sessionToken') {
                    
                    try {
                        const data = JSON.parse(localStorage.getItem(key));
                        allData.challenges.push({
                            storageKey: key,
                            sceneType: 'other',
                            data: data
                        });
                    } catch (error) {
                        console.warn(`Could not parse data for key ${key}:`, error);
                    }
                }
            }

            console.log("📊 Data collection complete:", {
                challenges: allData.challenges.length,
                reflections: allData.reflections.length,
                researchRecords: allData.researchData.length,
                friendsRescued: allData.friendsRescued.length
            });

        } catch (error) {
            console.error("❌ Error collecting game data:", error);
        }

        return allData;
    }

    async submitFinalScore() {
        console.log("🏆 Submitting final score to server...");
        console.log("🔍 Final submission context:");
        console.log(`  - SessionId: ${window.sessionId}`);
        console.log(`  - SessionToken: ${this.sessionToken ? 'AVAILABLE' : 'MISSING'}`);
        console.log(`  - Group: ${this.group}`);
        console.log(`  - Hostname: ${location.hostname}`);
        console.log(`  - Timestamp: ${new Date().toISOString()}`);

        // Collect all stored game data from localStorage
        console.log("📊 Collecting comprehensive game data...");
        const allGameData = this.collectAllGameData();

        // Submit final game completion data with all collected data
        const finaleData = {
            totalFriendsRescued: 5,
            totalTimeSpent: Math.round((Date.now() - window.sessionStart) / 1000), // Time in seconds
            completionTime: new Date().toISOString(),
            group: this.group,  // Add group info for tracking
            sessionId: window.sessionId,
            
            // Include all game data
            allChallenges: allGameData.challenges,
            allReflections: allGameData.reflections,
            researchData: allGameData.researchData,
            
            // Summary statistics
            totalChallengesCompleted: allGameData.challenges.length,
            totalReflectionsCompleted: allGameData.reflections.length,
            friendsRescuedList: allGameData.friendsRescued
        };

        console.log("📦 Finale data prepared:", {
            challenges: finaleData.allChallenges.length,
            reflections: finaleData.allReflections.length,
            friends: finaleData.friendsRescuedList.length
        });

        // Validate data completeness for Group 2 players
        if (this.group === 2) {
            const expectedReflections = finaleData.friendsRescuedList.length;
            const actualReflections = finaleData.allReflections.length;
            
            if (actualReflections < expectedReflections) {
                console.warn(`⚠️ Group 2 reflection data incomplete: ${actualReflections}/${expectedReflections} friends have reflection data`);
            } else {
                console.log(`✅ Group 2 reflection data complete: ${actualReflections}/${expectedReflections} friends`);
            }
        }

        console.log("📤 Submitting finale challenge data...");
        const finaleResult = await this.submitFinaleChallenge(finaleData);
        console.log(`📊 Finale submission result: ${finaleResult ? 'SUCCESS' : 'FAILED'}`);

        if (finaleResult) {
            console.log("✅ Complete game data successfully submitted in finale challenge");
        } else {
            console.error("❌ Finale submission failed - game data may be incomplete in database");
        }

        // Submit score to Mediamasters API if not localhost and we have an external sessionId
        if (location.hostname !== "localhost") {
            // Check if we have a Mediamasters sessionId (not our placeholder)
            const hasExternalSessionId = window.sessionId && 
                                       window.sessionId !== "0" && 
                                       window.sessionId !== "not set";
            
            if (hasExternalSessionId) {
                console.log('🎯 Submitting to Mediamasters with external sessionId:', window.sessionId);
                try {
                    const response = await fetch(`https://api.mediamasters.nl/extra-game/session-result?sessionId=${window.sessionId}`);
                    const data = await response.json();
                    console.log('✅ Score submitted successfully to Mediamasters:', data);
                } catch (error) {
                    console.error('❌ Error submitting score to Mediamasters:', error);
                } finally {
                    // End session after score submission attempt
                    this.end();
                }
            } else {
                console.log("ℹ️ No external sessionId found, skipping Mediamasters submission");
                console.log("   - Current sessionId:", window.sessionId);
                console.log("   - This is normal for direct game access without research context");
                this.end();
            }
        } else {
            console.log("🧪 Running on localhost, skipping Mediamasters score submission");
        }
        
        return true; // Always return true since our internal data storage is what matters most
    }

}
